import { useTranslation } from "react-i18next"

import { STATUS_AFFORDANCES } from "@/lib/motion"
import { cn } from "@/lib/utils"
import type { CrewPulse } from "@/types/collab"

interface LivePulseTickerProps {
  pulse?: CrewPulse
  className?: string
}

/**
 * OKS-472 — Crew Pulse live ticker (Figma `35:149`). A quietly-present
 * single-line strip — pulse dot + the most-recent crew event ("Maya just
 * posted") — that makes the home feel alive.
 *
 * ## V1 transport (TRD KD-1 / KD-7)
 *
 * V1 renders a STATIC snapshot from the brand-scoped `crew_pulse` prop; the
 * live transport (debounced `CreatorChannel.refetch` + poll-on-focus degrade)
 * is OKS-473's. The component MUST NOT break when updates are static — it
 * simply shows the latest event on each Inertia render.
 *
 * ## Resilience + a11y
 *
 * Renders NOTHING when there are zero events (UX Spec §3) or when the prop is
 * absent — the ticker "silently hides on its own error" and never blocks the
 * page. `aria-live="polite"` announces arrivals to SR users; the pulse dot
 * breathes via the CSS-only `live-status` affordance (OKS-562 — a status
 * indicator, NOT a vocabulary beat; reduced-motion-safe → static dot).
 *
 * Brand isolation: events come pre-scoped from `CrewPulseQuery` (first name +
 * avatar only, brand-internal) — this component renders text only.
 */
export function LivePulseTicker({ pulse, className }: LivePulseTickerProps) {
  const { t } = useTranslation()
  const latest = pulse?.events?.[0]

  if (!latest) return null

  const title = latest.payload?.title
  const text = title
    ? t("missions.pulse.posted_with_title", {
        name: latest.first_name,
        title,
      })
    : t("missions.pulse.posted", { name: latest.first_name })

  return (
    <div
      className={cn(
        "text-muted-foreground flex items-center gap-2 text-sm",
        className,
      )}
      data-testid="live-pulse-ticker"
      aria-live="polite"
      aria-label={t("missions.pulse.region_label")}
    >
      <span
        className={cn(
          "size-2 shrink-0 rounded-full bg-[#ff5c00]",
          STATUS_AFFORDANCES["live-status"],
        )}
        aria-hidden="true"
        data-testid="live-pulse-dot"
      />
      <span className="truncate">{text}</span>
    </div>
  )
}
